/*
  Scrolling rows of snippets/carousel-row.liquid. It measures whether the items overflow the row (data-overflow) or
  all fit (data-fits), keeps the arrows' disabled states in step with the scroll position, and moves the row by one
  item, one full view or three quarters of the row per click (data-step). In the theme editor it also brings a selected
  block into view (shopify:block:select); a carousel never moves on its own, so there is nothing to hold or resume on
  deselect.
*/

if (!customElements.get('carousel-row')) {
  /**
   * How far the row must scroll, in screen pixels (positive to the right), for an item to be fully in view: none when
   * it is, its start edge to the row's start when it is wider than the row.
   */
  const distanceToReveal = (item, view) => {
    if (item.width >= view.width) return item.left - view.left;
    if (item.left < view.left) return item.left - view.left;
    if (item.right > view.right) return item.right - view.right;
    return 0;
  };

  class CarouselRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel-row__track');
      // A grid never scrolls, so the script leaves it alone even when another row on the page has loaded it.
      if (!this.track || this.classList.contains('carousel-row--grid')) return;
      this.previous = this.querySelector('.carousel-row__arrow--previous');
      this.next = this.querySelector('.carousel-row__arrow--next');
      this.counters = this.querySelectorAll('[data-pager-current]');
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.scrolled = oncePerFrame(() => this.update());

      this.track.addEventListener('scroll', () => this.scrolled.schedule(), { passive: true, signal });
      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      // Theme editor: the item of the selected block is brought into view. The event bubbles from the block's element,
      // and the listeners go with the element (shopify:section:unload removes it), through the controller.
      this.addEventListener('shopify:block:select', (event) => this.reveal(event.target), { signal });
      // Fires once at the start, then whenever the row or one of its items changes size. What the row holds can grow
      // without the row growing (a picture arriving), and the row is a tab stop only while it overflows. The row's outer
      // box is the one watched, because that is what grows when its scroll bar appears.
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track, { box: 'border-box' });
      for (const item of this.track.children) this.resizeObserver.observe(item);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.scrolled?.cancel();
    }

    // Reads first and writes what changes the layout last, so measuring never makes the browser lay out the page again.
    measure() {
      // One pixel of slack absorbs subpixel rounding when the items exactly fill the row.
      const overflows = this.track.scrollWidth - this.track.clientWidth > 1;
      // The room the browser gives the scroll bar (none where bars are hidden or float): the arrows centre on the items
      // above it.
      const barRoom = this.track.offsetHeight - this.track.clientHeight;
      // Where the arrows are not drawn (touch screens in "on hover" mode), a keyboard user moves the row by
      // focusing its items. A row whose items hold nothing focusable becomes a tab stop itself, so the arrow keys
      // can scroll it, and so does a row that asks to be one (data-stop, which the markup also gives a tabindex so
      // it works without this script) while it overflows.
      reachScroller(this.track, { forced: this.track.hasAttribute('data-stop') });
      this.update();
      this.toggleAttribute('data-overflow', overflows);
      this.toggleAttribute('data-fits', !overflows);
      this.style.setProperty('--bar-room', `${barRoom}px`);
    }

    update() {
      const travelled = this.track.scrollLeft;
      const room = this.track.scrollWidth - this.track.clientWidth;
      if (this.counters.length) {
        const place = String(this.position(travelled) + 1);
        this.counters.forEach((counter) => (counter.textContent = place));
      }
      if (this.previous) this.previous.disabled = travelled <= 1;
      if (this.next) this.next.disabled = travelled >= room - 1;
    }

    /** One item and the gap after it, in pixels, measured on the first item shown (the caller may hide some). */
    span() {
      const items = this.track.querySelectorAll(':scope > .carousel-row__item');
      const first = Array.from(items).find((item) => item.offsetWidth > 0);
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    /** The item at the row's start, counting from 0 among those shown, for a row that shows one item at a time. */
    position(travelled) {
      const span = this.span();
      return span ? Math.max(0, Math.round(travelled / span)) : 0;
    }

    reveal(block) {
      const item = block instanceof Element ? block.closest('.carousel-row__item') : null;
      if (!item || item.parentElement !== this.track) return;
      const distance = distanceToReveal(item.getBoundingClientRect(), this.track.getBoundingClientRect());
      // Not smooth: the editor has just scrolled the page to the block, and the row should already be there.
      if (Math.abs(distance) > 1) this.track.scrollBy({ left: distance, behavior: 'instant' });
    }

    move(direction) {
      const span = this.span();
      if (!span) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      // A hair of tolerance, so four items that fill the row exactly count as four, not 3.999.
      const inView = Math.max(1, Math.floor((this.track.clientWidth + gap) / span + 0.01));
      let distance = span;
      if (this.dataset.step === 'page') distance = span * inView;
      else if (this.dataset.step === 'view') {
        // Three quarters of what the row shows: its width less the room it keeps around its items.
        const style = getComputedStyle(this.track);
        const shown =
          this.track.clientWidth - parseFloat(style.paddingInlineStart) - parseFloat(style.paddingInlineEnd);
        distance = shown * 0.75;
      }
      this.track.scrollBy({ left: direction * distance });
    }
  }

  customElements.define('carousel-row', CarouselRow);
}
